'use client'

import { layoutPadding } from '@/core/constants/class-names'
import { cn } from '@/core/lib/utils/cn'
import { RemoteImage } from '@/core/ui/atoms/remote-image'
import { Slider } from '@/core/ui/atoms/slider'
import { Triangles } from '@/core/ui/shapes'
import { BannerModel } from '@/features/content/types/models/banner-model'
import { Clock, Plane, Users } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'

const slideTags = [
  {
    icon: <Plane className='text-sky-blue' />,
    title: 'Airport Pickup',
  },
  {
    icon: <Clock className='text-sky-blue' />,
    title: '24/7',
  },
  {
    icon: <Users className='text-sky-blue' />,
    title: 'Professional Drivers',
  },
]

type Props = {
  staticHeader: React.JSX.Element
  fixedHeader: React.JSX.Element
  bannerSlides: BannerModel[]
  form: React.ReactNode
  textShadow?: boolean
}
export const BannerContent: React.FC<Props> = ({
  staticHeader,
  fixedHeader,
  bannerSlides,
  form,
  textShadow = false,
}) => {
  const [bannerBg, setBannerBg] = useState(bannerSlides?.[0]?.image.url ?? '')
  const [fixedHeaderShown, setFixedHeaderShown] = useState(false)
  const bannerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onScroll = () => {
      if (!bannerRef.current) return
      setFixedHeaderShown(window.scrollY > bannerRef.current.clientHeight - 250)
    }
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleSlideChange = (idx: number) => {
    const slide = bannerSlides[idx]
    if (slide) setBannerBg(slide.image.url)
  }

  return (
    <div
      className={cn('relative flex h-auto min-h-screen flex-col')}
      ref={bannerRef}
    >
      <div className='absolute inset-0 h-full overflow-hidden [clip-path:polygon(0%_0%,_100%_0%,_100%_80%,_0%_80%)] md:[clip-path:inset(0)]'>
        <div className='fixed inset-0 top-0 overflow-hidden will-change-transform'>
          <RemoteImage
            src={bannerBg}
            alt=''
            fill
            priority
            fetchPriority='high'
            className='absolute inset-0 object-cover object-left-top md:object-center'
          />
        </div>
        <div className='absolute inset-0 bg-black/50' />
      </div>
      {fixedHeaderShown ? fixedHeader : staticHeader}

      <div className='xs:block hidden h-16'>&nbsp;</div>
      <section
        className={cn(
          layoutPadding,
          'relative z-10 mt-8 flex h-full flex-1 flex-col items-center justify-between gap-8 pb-8 md:flex-row md:items-end md:pb-16',
        )}
      >
        <Slider
          className='sticky bottom-4 flex h-fit flex-1 flex-col justify-center self-center sm:items-center'
          slides={bannerSlides.map(slide => (
            <div key={slide.title} className='h-full'>
              <h1
                className={cn(
                  'text-red mb-2 w-full text-start font-medium whitespace-pre-line md:w-fit',
                  'text-3xl md:text-5xl',
                )}
                dangerouslySetInnerHTML={{
                  __html: slide.title,
                }}
              />

              <div
                className={cn(
                  'mt-1 hidden text-center text-white sm:inline-block md:w-fit md:text-start md:text-lg',
                  textShadow &&
                    'rounded-sm bg-black/70 p-1.5 shadow-sm shadow-black',
                )}
                dangerouslySetInnerHTML={{ __html: slide.description }}
              />

              <ul className='mt-8 flex flex-wrap gap-3'>
                {slideTags.map(tag => (
                  <li
                    key={tag.title}
                    className='flex items-center gap-1.5 rounded-xl bg-black/70 px-3 py-1 text-sm font-medium text-white'
                  >
                    {tag.icon}
                    {tag.title}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          onSlideChange={handleSlideChange}
        />
        <div className='shadow-grey-60/80 h-full w-full max-w-md flex-1 rounded shadow-sm md:shadow-none'>
          {form}
        </div>
      </section>
      <Triangles className='absolute bottom-0 hidden translate-y-10 md:block' />
    </div>
  )
}
